import {
  apiQuery,
  apiListQuery,
  createQueryKeys,
  useApiMutation,
  type ApiMutationOptions,
  type ApiMutationResult,
} from '@/shared/data';
import { getCurrentTenant } from '@/shared/http';
import * as api from './payroll-agreement.api';
import type { PayrollAgreementListParams, PayrollAgreementWrite } from './payroll-agreement.wire';
export const payrollAgreementKeys = createQueryKeys('payroll-agreement');
export const payrollAgreementDetailQuery = (id: string) =>
  apiQuery(payrollAgreementKeys.detail(id), ({ signal }) => api.getPayrollAgreement(id, signal), {
    enabled: id !== '' && id !== 'novo',
  });
export const payrollAgreementOptionsQuery = (p: PayrollAgreementListParams = {}) => {
  const params = { ...p, tenant: p.tenant ?? getCurrentTenant() ?? undefined };
  return apiListQuery(payrollAgreementKeys.list(params), ({ signal }) =>
    api.listPayrollAgreements(params, signal),
  );
};
export const employeePositionsQuery = (query: string) =>
  apiQuery(payrollAgreementKeys.custom('cbo', query.length >= 2 ? query : 'admin'), ({ signal }) =>
    api.listEmployeePositions(query, signal),
  );
export const payrollAgreementMutations = {
  create: (body: PayrollAgreementWrite) => api.createPayrollAgreement(body),
  update: ({ id, body }: { id: string; body: PayrollAgreementWrite }) =>
    api.updatePayrollAgreement(id, body),
  delete: ({ id }: { id: string }) => api.deletePayrollAgreement(id),
};
type Name = keyof typeof payrollAgreementMutations;
type Variables<K extends Name> = Parameters<(typeof payrollAgreementMutations)[K]>[0];
export function usePayrollAgreementMutation<K extends Name>(
  name: K,
  options: Omit<ApiMutationOptions<unknown, Variables<K>>, 'mutationFn'> = {},
): ApiMutationResult<unknown, Variables<K>> {
  const run = payrollAgreementMutations[name] as (variables: Variables<K>) => Promise<unknown>;
  return useApiMutation({
    mutationKey: ['payroll-agreement', 'mutation', name],
    invalidate: [payrollAgreementKeys.all],
    success: false,
    error: false,
    ...options,
    mutationFn: run,
  });
}
